//@ts-check
import _ from 'lodash';
import { NotFoundError, NotImplementedError } from './Error';
import { validationByJoi } from '../../utilities/commonValidation';
/*
LINE WOKRSモーダル内のLINE WORKSユーザーを扱うクラスや関数。
「_」付きの変数や関数を直接取得・変更してはならない。
(プライベート宣言はESLint非対応のため未使用)
*/

class LwUser {
  _parameter = {};

  /**
   * LINE WROKSユーザーの情報を格納するクラス
   * @param {any} parameter
   * 留意事項
   * parameterとしては以下のような値が入ることを想定している。
   * これはRailsAPIから取得できるデータそのままである。
   * {
   *   "id": 203834,
   *   "line_works_id": "bld203834",
   *   "company_id": 32839,
   *   "company_name": "テスト株式会社",
   *   "role_code": 2,
   *   "role_name": "本店管理者",
   *   "fullname": "山田 太郎",
   *   "is_email": true
   * }
   **/
  // APIから渡ってくる値を入れるので、厳密にチェックしない
  constructor(parameter) {
    this._parameter = _.cloneDeep(parameter);
    if (
      !(
        _.has(this._parameter, 'id') &&
        _.has(this._parameter, 'line_works_id') &&
        _.has(this._parameter, 'company_name') &&
        _.has(this._parameter, 'role_code') &&
        _.has(this._parameter, 'role_name') &&
        _.has(this._parameter, 'fullname') &&
        _.has(this._parameter, 'is_email')
      )
    ) {
      throw NotFoundError;
    }
    Object.freeze(this._parameter);
  }

  /** @return {number} LINE WROKSユーザーのID **/
  get id() {
    return this._parameter.id;
  }

  /** @return {string} LINE WROKSユーザーの表示用ID **/
  get line_works_id() {
    return this._parameter.line_works_id;
  }

  /** @return {string} LINE WROKSユーザーが所属している会社名 **/
  get company_name() {
    return this._parameter.company_name;
  }

  /** @return {string} LINE WROKSユーザーの権限名 **/
  get role_name() {
    return this._parameter.role_name;
  }

  /** @return {string} LINE WROKSユーザーのフルネーム **/
  get full_name() {
    return this._parameter.fullname;
  }

  /**
   * LINE WORKSユーザーのエラーメッセージリストを取得する
   * @param {boolean} isSso - SSOを利用しているか否か
   * 必要であれば子クラスで中身を実装すること
   **/
  // eslint-disable-next-line no-unused-vars
  getErrorMessageList(isSso) {
    /** @type {string[]} */
    const result = [];
    return Object.freeze(result);
  }
}

class LwUsers {
  /** @type {LwUser[] } **/
  _lwUserList = [];

  /**
   * 複数のLINE WROKSユーザーの情報を保持するコレクションクラス
   * @param {LwUser[]} lw_user_list
   **/
  constructor(lw_user_list) {
    const sortList = _.sortBy(_.cloneDeep(lw_user_list), user => user.id);
    this._lwUserList = sortList;
    Object.freeze(this._lwUserList);
  }

  /** @return {LwUser[] } LINE WROKSユーザーのリスト **/
  get list() {
    return this._lwUserList;
  }

  /** @return {Readonly<number[]>} LINE WROKSユーザーのIDリスト **/
  get ids() {
    return Object.freeze(
      _.map(this._lwUserList, function(user) {
        return user.id;
      })
    );
  }

  /** @return {Readonly<number[]>} LINE WROKSユーザーの正常なIDリスト **/
  getNormalIds(isSso) {
    return Object.freeze(
      _.flatMap(this._lwUserList, function(user) {
        const error_message_list = user.getErrorMessageList(isSso);
        if (_.isEmpty(error_message_list)) {
          return [user.id];
        } else {
          return [];
        }
      })
    );
  }

  /**
   * @param {number} id LINE WORKSユーザーのID
   * @return {LwUser} LINE WROKSユーザークラス
   **/
  getUser(id) {
    const result = _.filter(this._lwUserList, function(user) {
      return user.id === id;
    });
    if (result.length !== 1) {
      // 型情報がおかしいが、正常にエラーを呼び出せるので無視する
      // @ts-ignore
      throw new NotFoundError();
    }
    // idは重複しないので、その中身を返す
    return result[0];
  }

  /**
   * @param {LwUser[]} addOtherUsersList 追加しようとしている他のユーザークラスのリスト
   * @param {typeof LwUser} createUserClass 作成しようとしているユーザークラス
   * @param {typeof LwUsers} createUserCollectionClass 作成しようとしているユーザーコレクションクラス
   * @return {LwUsers} ユーザ追加後のLINE WROKSユーザークラス
   * @protected
   **/
  _addOtherUsers(
    addOtherUsersList,
    createUserClass,
    createUserCollectionClass
  ) {
    /** @type {LwUser[]} **/
    const addLwUserList = [];
    for (const otherUserData of addOtherUsersList) {
      addLwUserList.push(new createUserClass(otherUserData._parameter));
    }
    const lw_user_list = addLwUserList.concat(this._lwUserList);
    return new createUserCollectionClass(lw_user_list);
  }

  /**
   * @param {LwUser[]} addOtherUsersList 追加しようとしている他のユーザークラスのリスト
   * @return {LwUsers} ユーザ追加後のLINE WROKSユーザークラス
   **/
  // eslint-disable-next-line no-unused-vars
  addOtherUsers(addOtherUsersList) {
    // ユーザーの交換が発生するLINE WORKSユーザーコレクションクラスの場合、子クラスで実装すること
    // 型情報がおかしいが、正常にエラーを呼び出せるので無視する
    // @ts-ignore
    throw new NotImplementedError();
  }

  /**
   * @param {number[]} lwUserIds 削除しようとしているユーザーのID
   * @param {typeof LwUsers} createUserCollectionClass 作成しようとしているユーザーコレクションクラス
   * @return {LwUsers} ユーザ削除後のLINE WROKSユーザークラス
   * @protected
   **/
  _removeUsers(lwUserIds, createUserCollectionClass) {
    const lwUserList = _.reject(this._lwUserList, function(lwUser) {
      return lwUserIds.includes(lwUser.id);
    });
    return new createUserCollectionClass(lwUserList);
  }
  /**
   * @param {number[]} lwUserIds 削除しようとしているユーザーのID配列
   * @return {LwUsers} ユーザ削除後のLINE WROKSユーザークラス
   **/
  // eslint-disable-next-line no-unused-vars
  removeUsers(lwUserIds) {
    // ユーザーの交換が発生するLINE WORKSユーザーコレクションクラスの場合、子クラスで実装すること
    // 型情報がおかしいが、正常にエラーを呼び出せるので無視する
    // @ts-ignore
    throw new NotImplementedError();
  }

  /** @return {boolean} 空か否か **/
  isEmpty() {
    return _.isEmpty(this._lwUserList);
  }
}

/**
 * 二つのLINE WORKSユーザーリスト間でLINE WORKSユーザーを交換する
 * @param {LwUsers} firstLwUsers 一つ目のユーザーコレクションクラス
 * @param {LwUsers} secondLwUsers 二つ目のユーザーコレクションクラス
 * @param {number[]} lwUserIdsFromFirstToSecond 一つ目から二つ目に移動させるユーザーIDのリスト
 * @param {number[]} lwUserIdsFromSecondToFirst 二つ目から一つ目に移動させるユーザーIDのリスト
 * @return {{ new_first_lw_users: LwUsers, new_second_lw_users: LwUsers }} ユーザ移動後のLINE WROKSユーザーコレクションクラス
 **/
function swapLwUsersBetweenLwUserList(
  firstLwUsers,
  secondLwUsers,
  lwUserIdsFromFirstToSecond,
  lwUserIdsFromSecondToFirst
) {
  /** @type {LwUser[]} **/
  const lwUserListFromFirstToSecond = [];
  for (let userId of lwUserIdsFromFirstToSecond) {
    try {
      const userData = firstLwUsers.getUser(userId);
      lwUserListFromFirstToSecond.push(userData);
    } catch (error) {
      if (error instanceof NotFoundError) {
        // 存在しないIDが移動候補に上がった場合、正常動作として続行する。
        continue;
      } else {
        throw error;
      }
    }
  }
  /** @type {LwUser[]} **/
  const lwUserListFromSecondToFirst = [];
  for (let userId of lwUserIdsFromSecondToFirst) {
    try {
      const userData = secondLwUsers.getUser(userId);
      lwUserListFromSecondToFirst.push(userData);
    } catch (error) {
      if (error instanceof NotFoundError) {
        // 存在しないIDが移動候補に上がった場合、正常動作として続行する。
        continue;
      } else {
        throw error;
      }
    }
  }
  try {
    const newFirstLwUsers = firstLwUsers
      .addOtherUsers(lwUserListFromSecondToFirst)
      .removeUsers(lwUserIdsFromFirstToSecond);
    const newSecondLwUsers = secondLwUsers
      .addOtherUsers(lwUserListFromFirstToSecond)
      .removeUsers(lwUserIdsFromSecondToFirst);
    return {
      new_first_lw_users: newFirstLwUsers,
      new_second_lw_users: newSecondLwUsers,
    };
  } catch (e) {
    // 未定義のユーザーコレクションクラスが来たときは、何もせず返す
    // データ読み込み前に動いたとき用の避難ハッチ
    if (e instanceof NotImplementedError) {
      const newFirstLwUsers = firstLwUsers;
      const newSecondLwUsers = secondLwUsers;
      return {
        new_first_lw_users: newFirstLwUsers,
        new_second_lw_users: newSecondLwUsers,
      };
    } else {
      throw e;
    }
  }
}

class EditName {
  _editName = '';
  _isInvalid = false;
  _errorMessage = '';
  _schema = '';

  /**
   * 編集が必要になる名前を格納するクラス
   * @param {string} editName - 編集する名前
   * @param {any} schema - スキーマ
   **/
  constructor(editName, schema) {
    this._editName = editName;
    const result = validationByJoi(schema, { name: editName });
    this._isInvalid = !result.status;
    if (this._isInvalid) {
      this._errorMessage = result.messages.name;
    }
  }

  get value() {
    return this._editName;
  }

  /**
   * @param {string} editName - 編集する名前
   * @param {typeof EditName} childrenClass - 子クラス
   * @param {any} schema - スキーマ
   * @return {EditName}
   * @protected
   **/
  _setValue(editName, childrenClass, schema) {
    if (this._editName === editName) {
      return this;
    }
    return new childrenClass(editName, schema);
  }

  /**
   * @param {string} editName - 編集する名前
   * @return {EditName}
   **/
  // eslint-disable-next-line no-unused-vars
  setValue(editName) {
    // 子クラスで実装必須
    // 型情報がおかしいが、正常にエラーを呼び出せるので無視する
    // @ts-ignore
    throw new NotImplementedError();
  }

  isInvalid() {
    return this._isInvalid;
  }

  getErrorMessage() {
    return this._errorMessage;
  }
}

export { LwUser, LwUsers, EditName, swapLwUsersBetweenLwUserList };



// WEBPACK FOOTER //
// ./src/services/lwUser/Base.js